import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import EntitySearchForm from '../../../components/EntitySearchForm';
import Pagination from '../../../components/Pagination';
import client from '../../../lib/feathers';

const STATUS_LIST = ['active', 'unsubscribed', 'bounced', 'invalid'];

const SOURCE_LIST = [
  { value: 'api_existing', label: 'API Existing' },
  { value: 'crawl_cache', label: 'Crawl Cache' },
  { value: 'brave_search', label: 'Brave Search' },
  { value: 'google_places', label: 'Google Places' },
  { value: 'manual', label: 'Manual' },
];

const statusBadgeStyles = {
  active: 'bg-green-100 text-green-800',
  unsubscribed: 'bg-yellow-100 text-yellow-800',
  bounced: 'bg-red-100 text-red-800',
  invalid: 'bg-gray-100 text-gray-800',
};

const sourceBadgeStyles = {
  api_existing: 'bg-blue-100 text-blue-800',
  crawl_cache: 'bg-purple-100 text-purple-800',
  brave_search: 'bg-orange-100 text-orange-800',
  google_places: 'bg-emerald-100 text-emerald-800',
  manual: 'bg-gray-100 text-gray-800',
};

const getSourceLabel = (value) => {
  const match = SOURCE_LIST.find((s) => s.value === value);
  return match ? match.label : value;
};

export default function OutreachContacts() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;

  const [page, setPage] = useState(Number(query.get('page')) || 0);
  const [statusFilter, setStatusFilter] = useState(query.get('status') || undefined);
  const [sourceFilter, setSourceFilter] = useState(query.get('source') || '');
  const [keywords, setKeywords] = useState(query.get('email') || '');

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Add contact modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ listingId: '', email: '', name: '', role: '', source: 'manual' });
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  const getContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: { createdAt: -1 },
      };

      if (statusFilter) {
        q.status = statusFilter;
      }

      if (sourceFilter) {
        q.source = sourceFilter;
      }

      if (keywords && keywords.length) {
        q.email = { $iLike: `%${keywords}%` };
      }

      const result = await client.service('admin/outreach-contact').find({ query: q });
      setContacts(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Error loading outreach contacts:', e);
      setError('There was a problem loading contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    getContacts();
  };

  const navigateToPage = (p = 0) => {
    if (p * limit > total || p * limit < 0) return;
    navigate(`/admin/outreach/contacts?page=${p}`);
    setPage(p);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status || undefined);
    setPage(0);
  };

  const handleSourceFilter = (e) => {
    setSourceFilter(e.target.value);
    setPage(0);
  };

  useEffect(() => {
    getContacts();
  }, [page, statusFilter, sourceFilter]);

  const handleImportFromListings = async () => {
    if (!window.confirm('Import contacts from all listings that have an email address? Existing contacts will be skipped.')) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await client.service('admin/outreach-contact').create({ action: 'importFromListings' });
      setImportResult(result);
      getContacts();
    } catch (e) {
      setError('Import failed: ' + (e.message || 'Unknown error'));
    } finally {
      setImporting(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    setAddError(null);
    setAddLoading(true);
    try {
      await client.service('admin/outreach-contact').create({
        listingId: addForm.listingId,
        email: addForm.email,
        name: addForm.name || undefined,
        role: addForm.role || undefined,
        source: addForm.source,
      });
      setShowAddModal(false);
      setAddForm({ listingId: '', email: '', name: '', role: '', source: 'manual' });
      getContacts();
    } catch (e) {
      setAddError(e.message || 'Failed to add contact');
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <Page title="Outreach Contacts">

      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb
            elements={[
              { name: 'Outreach', href: '/admin/outreach/campaigns' },
              { name: 'Contacts' },
            ]}
          />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4 flex gap-2">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Add Contact
          </button>
          <button
            type="button"
            onClick={handleImportFromListings}
            disabled={importing}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-800 ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {importing ? 'Importing...' : 'Import from Listings'}
          </button>
        </div>
      </div>

      <div className="pb-4 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Contacts ({total} Total)
        </h3>
        <div className="mt-3 sm:mt-0 flex items-center gap-4">
          <EntitySearchForm onSearch={onSearch} setKeywords={setKeywords} keywords={keywords} />
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>

      <div className="py-3 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <div className="sm:hidden">
            <label htmlFor="status-select" className="sr-only">Filter by status</label>
            <select
              id="status-select"
              value={statusFilter || ''}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md"
            >
              <option value="">All Statuses</option>
              {STATUS_LIST.map((s) => (
                <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6" aria-label="Status filter">
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleStatusFilter(undefined); }}
                  className={`capitalize whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${!statusFilter ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  All
                </a>
                {STATUS_LIST.map((s) => (
                  <a
                    key={s}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleStatusFilter(s); }}
                    className={`capitalize whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${statusFilter === s ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    {s}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="sm:w-48">
          <label htmlFor="source-select" className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Source
          </label>
          <select
            id="source-select"
            value={sourceFilter}
            onChange={handleSourceFilter}
            className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 rounded-md"
          >
            <option value="">All Sources</option>
            {SOURCE_LIST.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {importResult && (
        <div className="rounded-md bg-green-50 p-4 mb-4">
          <p className="text-sm text-green-700">
            Import complete: <strong>{importResult.created}</strong> contacts created, <strong>{importResult.skipped}</strong> skipped (of {importResult.total} listings with email).
          </p>
          <button onClick={() => setImportResult(null)} className="text-xs text-green-600 underline mt-1">Dismiss</button>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name / Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Listing
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        Loading contacts...
                      </td>
                    </tr>
                  )}
                  {!loading && contacts.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        No contacts found.
                      </td>
                    </tr>
                  )}
                  {!loading && contacts.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      // /admin/outreach/contacts/:id has never had a route:
                      // the detail page lives at /admin/contacts/:id. Every
                      // row click landed on a blank page, which made the
                      // whole contact record unreachable from here.
                      onClick={() => navigate(`/admin/contacts/${c.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {c.name || <span className="text-gray-400 italic">No name</span>}
                        </div>
                        <div className="text-sm text-gray-500">{c.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {c.listing ? (
                          <Link
                            to={`/admin/listings/${c.listing.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm text-gray-900 hover:text-primary hover:underline"
                          >
                            {c.listing.name}
                          </Link>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${sourceBadgeStyles[c.source] || 'bg-gray-100 text-gray-800'}`}>
                          {getSourceLabel(c.source)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${statusBadgeStyles[c.status] || 'bg-gray-100 text-gray-800'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {format(new Date(c.createdAt), 'MMM d, yyyy')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/admin/outreach/contacts/${c.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:text-red-900"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAddModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Contact</h3>
              <form onSubmit={handleAddContact} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Listing ID *</label>
                  <input
                    type="text"
                    required
                    value={addForm.listingId}
                    onChange={(e) => setAddForm({ ...addForm, listingId: e.target.value })}
                    placeholder="UUID of the listing"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    required
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <input
                    type="text"
                    value={addForm.role}
                    onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                    placeholder="e.g. owner, winemaker, general"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Source</label>
                  <select
                    value={addForm.source}
                    onChange={(e) => setAddForm({ ...addForm, source: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    {SOURCE_LIST.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                {addError && (
                  <div className="rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-700">{addError}</p>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); setAddError(null); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addLoading}
                    className={`px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-red-800 ${addLoading ? 'opacity-50' : ''}`}
                  >
                    {addLoading ? 'Adding...' : 'Add Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </Page>
  );
}
